import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BarChart3, ArrowRight } from 'lucide-react';

const STATUSES = ['pending', 'reviewed', 'shortlisted', 'rejected', 'withdrawn'] as const;

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  reviewed: 'bg-blue-100 text-blue-800 border-blue-200',
  shortlisted: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  withdrawn: 'bg-muted text-muted-foreground border-border',
};

interface Props {
  professionalId: string;
}

export function ApplicationStatsCard({ professionalId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      const { data } = await supabase
        .from('applications')
        .select('status')
        .eq('professional_id', professionalId);

      if (data) {
        const map: Record<string, number> = {};
        data.forEach(a => { map[a.status] = (map[a.status] || 0) + 1; });
        setCounts(map);
        setTotal(data.length);
      }
    };
    fetchStats();
  }, [professionalId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <BarChart3 className="h-5 w-5 text-primary" />
          {t('myProfile.applicationStats')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {total === 0 ? (
          <p className="text-sm text-muted-foreground">{t('myProfile.noApplications')}</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {STATUSES.map(s => (
              <div key={s} className={`rounded-lg border px-3 py-2 text-center min-w-[80px] ${STATUS_COLORS[s]}`}>
                <p className="text-lg font-bold">{counts[s] || 0}</p>
                <p className="text-xs capitalize">{t(`applications.status.${s}`)}</p>
              </div>
            ))}
          </div>
        )}
        <Button variant="outline" size="sm" onClick={() => navigate('/applications')}>
          {t('myProfile.viewAllApplications')}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
